import { useState, useEffect } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MapPin, Car, DoorOpen, ListChecks, Info, Navigation, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { STATUSES, WEEKDAYS } from "@/lib/calendarUtils";
import { confirm } from "@/components/ConfirmDialog";
import { FIELD_JOB_TYPES, jobTypeLabel } from "@/lib/fieldTypes";
import { WorkerMultiSelect } from "@/components/WorkerMultiSelect";
import { StaffingBadge } from "@/components/StaffingBadge";
import { ShiftClockIn } from "@/components/schedule/ShiftClockIn";
import { DayNotices } from "@/components/schedule/DayNotices";
import { JobNumberLink } from "@/components/JobNumberLink";
import { MapLink } from "@/components/MapLink";
import { Link } from "react-router-dom";
import JobProfile from "@/pages/JobProfile";
import { NeverEnds } from "@/components/schedule/NeverEnds";

const UNASSIGNED = "__unassigned__";

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-sm">
      <Icon className="h-4 w-4 text-brand shrink-0 mt-0.5" />
      <div><span className="text-slate-500">{label}: </span><span className="text-slate-800">{value}</span></div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="border-b border-slate-100 pb-3 last:border-0" data-testid={testid}>
      <p className="text-sm font-bold text-slate-900 mb-1">{title}</p>
      {children}
    </div>
  );
}

const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Touch-friendly way to move a job/shift to another day (drag needs a mouse).
function MoveBar({ event, busy, onMove }) {
  const [date, setDate] = useState(event.date);
  useEffect(() => { setDate(event.date); }, [event.date]);
  const shift = (n) => { const d = new Date(`${date}T00:00:00`); d.setDate(d.getDate() + n); setDate(isoDay(d)); };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="summary-move">
      <p className="text-sm font-bold text-slate-900 mb-2">Move to another day</p>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => shift(-1)} data-testid="summary-move-prev">− 1 day</Button>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 flex-1 bg-white" data-testid="summary-move-date" />
        <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => shift(1)} data-testid="summary-move-next">+ 1 day</Button>
      </div>
      <Button type="button" disabled={busy || !date || date === event.date} onClick={() => onMove(date)}
        className="mt-2 w-full bg-brand hover:bg-brand-dark" data-testid="summary-move-save">
        {date && date !== event.date ? `Move to ${fmtDay(date)}` : "Pick a new day"}
      </Button>
    </div>
  );
}

function NumberBanner({ event, isJob }) {
  const n = isJob ? event.jobNumber : event.siteNumber;
  return (
    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 ${isJob ? "bg-orange-50 border border-orange-200" : "bg-teal-50 border border-teal-200"}`} data-testid="summary-number-banner">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{isJob ? "Job number" : "Site number"}</span>
      {n == null ? <span className="text-sm text-slate-400" data-testid="summary-number-missing">Not numbered yet</span>
        : isJob ? <JobNumberLink n={n} className="text-lg" testid="summary-job-number-big" />
          : <span className="font-mono text-lg font-semibold text-teal-700" data-testid="summary-site-number-big">#S{n}</span>}
    </div>
  );
}

function EventSummary({ event, isJob, canClient, editable, busy, onToggleDone }) {
  const op = event.operational || {};
  const done = event.status === "completed";
  return (
    <div className="space-y-3" data-testid="event-summary">
      <NumberBanner event={event} isJob={isJob} />
      {editable && event.status !== "cancelled" && (
        <label className="flex items-center gap-3 border-b border-slate-100 pb-3 cursor-pointer">
          <Checkbox checked={done} disabled={busy} onCheckedChange={onToggleDone} data-testid="summary-completed-checkbox" className="h-5 w-5" />
          <span className="text-base text-slate-800">Completed</span>
        </label>
      )}
      {event.status === "cancelled" && <p className="text-sm font-semibold text-red-600">Cancelled{event.cancellation?.reason ? ` — ${event.cancellation.reason}` : ""}</p>}
      <Section title="Details" testid="summary-details">
        {isJob ? (
          <p className="text-sm font-semibold text-brand">
            {event.clientName && (canClient && event.clientId
              ? <Link to={`/clients/${event.clientId}`} className="hover:underline" data-testid="event-client-link">{event.clientName}</Link>
              : <span data-testid="event-client-name">{event.clientName}</span>)}
            {event.clientName && event.jobNumber ? " — " : ""}
            {event.jobNumber && <>Job <JobNumberLink n={event.jobNumber} testid="summary-job-number-link" /></>}
          </p>
        ) : <p className="text-sm font-semibold text-teal-700">{event.siteNumber ? `#S${event.siteNumber} · ` : ""}{event.siteName}</p>}
        {isJob && <p className="text-sm text-slate-700">{jobTypeLabel((k, d) => d, event.jobType)}{event.jobType === "other" && event.jobTypeOther ? ` · ${event.jobTypeOther}` : ""}</p>}
        {event.addressDetails && <p className="text-sm text-slate-700">{event.addressDetails}</p>}
        {isJob && event.jobInfo && <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="event-job-info">{event.jobInfo}</p>}
        {event.notes && <p className="text-sm text-slate-600 whitespace-pre-wrap">{event.notes}</p>}
      </Section>
      <Section title="Team" testid="summary-team">
        {event.workers?.length || event.workerName ? (
          <div className="flex flex-wrap gap-1.5">
            {[...(event.workers?.length ? event.workers : [{ id: event.workerId, name: event.workerName }])]
              .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })).map((w) => (
              <span key={w.id || w.name} className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-800">{w.name}</span>
            ))}
          </div>
        ) : <p className="text-sm font-semibold text-red-600">Unassigned</p>}
      </Section>
      <Section title="Location" testid="summary-location">
        <p className="text-sm"><MapLink address={op.address || event.address} geo={op.geo || event.geo} testid="event-address-link" /></p>
      </Section>
      <div className="flex justify-between gap-4" data-testid="summary-times">
        <div><p className="text-sm font-bold text-slate-900">Starts</p><p className="text-sm text-slate-700">{fmtDay(event.date)}</p><p className="text-lg font-semibold text-slate-900">{event.startTime}</p></div>
        <div className="text-right"><p className="text-sm font-bold text-slate-900">Ends</p><p className="text-sm text-slate-700">{fmtDay(event.date)}</p><p className="text-lg font-semibold text-slate-900">{event.endTime}</p></div>
      </div>
    </div>
  );
}

export default function EventDetailDialog({ event, onOpenChange, workers, can, onUpdated, onDuplicate, onQueue }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showJob, setShowJob] = useState(false);
  const [form, setForm] = useState(null);
  const [applySeries, setApplySeries] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showRecur, setShowRecur] = useState(false);
  const [rec, setRec] = useState({ freq: "weekly", interval: 1, byWeekday: [], count: 4, until: "" });

  useEffect(() => {
    if (event) {
      setForm({ workerId: event.workerId || UNASSIGNED,
        workerIds: event.workerIds || (event.workerId ? [event.workerId] : []),
        requiredWorkerCount: event.requiredWorkerCount || 1,
        date: event.date, startTime: event.startTime, endTime: event.endTime, status: event.status });
      setApplySeries(false); setCancelReason(""); setShowRecur(false); setEditMode(false); setShowJob(false);
      const r = event.recurrence || {};
      setRec({ freq: r.freq || "weekly", interval: r.interval || 1, byWeekday: r.byWeekday || [], count: r.count || 4, until: r.until || "", forever: !!r.forever });
    }
  }, [event]);

  if (!event || !form) return null;
  const isJob = event.kind === "job";
  const isGroup = !isJob && !!event.shiftIds;
  const editable = isJob ? can("job.manage") : can("schedule.edit");
  const op = event.operational || {};

  const toggleWd = (d) => setRec((r) => ({ ...r, byWeekday: r.byWeekday.includes(d) ? r.byWeekday.filter((x) => x !== d) : [...r.byWeekday, d] }));

  const remove = async () => {
    const series = applySeries && event.seriesId;
    if (!(await confirm({ title: t("cal.deleteTitle", "Delete event"), description: series ? t("cal.delSeriesConfirm", "Delete this and all future occurrences?") : t("cal.delOneConfirm", "Delete this occurrence?"), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    setBusy(true);
    try {
      const { data } = isGroup
        ? await client.post("/calendar/shift-group/delete", { shiftIds: event.shiftIds, applyToSeries: !!series })
        : await client.delete(`/calendar/event/${event.kind}/${event.id}`, { params: { applyToSeries: !!series } });
      toast.success(`${t("cal.deleted", "Deleted")}${data.deleted > 1 ? ` (${data.deleted})` : ""}`);
      onUpdated(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const rescheduleSeries = async () => {
    setBusy(true);
    try {
      const rule = { freq: rec.freq, interval: Number(rec.interval) || 1,
        byWeekday: rec.freq === "weekly" ? rec.byWeekday : [],
        count: rec.forever || rec.until ? null : Number(rec.count) || 1, until: rec.forever ? null : rec.until || null,
        forever: !!rec.forever };
      const { data } = await client.post(`/calendar/event/${event.kind}/${event.id}/reschedule-series`, rule);
      toast.success(`${t("cal.repeatUpdated", "Repeat updated from here")} (${data.created})`);
      onUpdated(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const moveTo = async (date) => {
    setBusy(true);
    try {
      const body = { date, startTime: event.startTime, endTime: event.endTime, applyToSeries: false };
      if (isGroup) await client.patch("/calendar/shift-group", { ...body, shiftIds: event.shiftIds });
      else await client.patch(`/calendar/event/${event.kind}/${event.id}`, body);
      toast.success(t("toast.eventUpdated", "Updated"));
      onUpdated();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const patch = async (overrides = {}, keepOpen = false) => {
    setBusy(true);
    try {
      const body = {
        date: form.date, startTime: form.startTime, endTime: form.endTime,
        applyToSeries: applySeries, ...overrides,
      };
      if (isJob) body.workerIds = form.workerIds;
      else body.workerId = form.workerId === UNASSIGNED ? null : form.workerId;
      if (isJob) body.requiredWorkerCount = form.requiredWorkerCount;
      if (body.status === "cancelled") body.cancellationReason = cancelReason;
      if (isGroup) {
        const { workerId, ...rest } = body; // eslint-disable-line no-unused-vars
        const { data } = await client.patch("/calendar/shift-group", { ...rest, shiftIds: event.shiftIds, workerIds: overrides.status ? undefined : form.workerIds });
        toast.success(`${t("toast.eventUpdated", "Updated")}${data.affected > 1 ? ` (${data.affected})` : ""}`);
        onUpdated(keepOpen, overrides);
        return;
      }
      const { data } = await client.patch(`/calendar/event/${event.kind}/${event.id}`, body);
      toast.success(`${t("toast.eventUpdated", "Updated")}${data.affected > 1 ? ` (${data.affected})` : ""}`);
      onUpdated(keepOpen, overrides);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open={!!event} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-detail-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Badge className={isJob ? "bg-brand" : "bg-teal-600"}>{isJob ? t("field.label", "iClean Field") : t("connect.label", "iClean Connect")}</Badge>
            {event.cover && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700" data-testid="event-cover-badge">Cover</Badge>}
            {isJob && <Badge variant="outline" className="border-brand/40 text-brand">{jobTypeLabel(t, event.jobType)}{event.jobType === "other" && event.jobTypeOther ? ` · ${event.jobTypeOther}` : ""}</Badge>}
            {isJob && event.jobNumber ? <><JobNumberLink n={event.jobNumber} testid="event-job-number-link" /> {event.propertyAddress || event.address}</> : event.title}
            {event.seriesId && <Badge variant="outline" className="border-brand/40 text-brand">{event.recurrence?.forever ? t("cal.neverEndsBadge", "Repeats forever") : t("cal.recurringBadge", "Recurring")}</Badge>}
          </DialogTitle>
          <DialogDescription>{event.date} · {event.startTime}–{event.endTime} · {t(`st.${event.status}`, event.status)}</DialogDescription>
        </DialogHeader>

        {!editMode ? (
          <>
            <EventSummary event={event} isJob={isJob} canClient={can("client.view")} editable={editable} busy={busy}
              onToggleDone={(v) => patch({ status: v ? "completed" : "scheduled" }, true)} />
            {editable && ["scheduled", "active"].includes(event.status) && (
              <MoveBar event={event} busy={busy} onMove={moveTo} />
            )}
            <ShiftClockIn event={event} />
            <DayNotices event={event} />
            <div className="grid grid-cols-2 gap-3 pt-2" data-testid="summary-actions">
              {editable ? (
                <Button variant="outline" className="h-11 text-base font-semibold text-brand border-slate-300" onClick={() => setEditMode(true)} data-testid="summary-edit-btn">Edit</Button>
              ) : !isJob ? null : <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>Close</Button>}
              {isJob ? (
                <Button className="h-11 text-base font-semibold bg-brand hover:bg-brand-dark" onClick={() => setShowJob(true)} data-testid="summary-view-details-btn">
                  View Details
                </Button>
              ) : <Button variant="ghost" className="h-11" onClick={() => onOpenChange(false)}>Close</Button>}
            </div>
          </>
        ) : (
        <>

        <div className="space-y-2 rounded-lg bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("cal.operational", "Operational info")}</p>
          <Row icon={MapPin} label={t("prop.address", "Address")} value={op.address || op.geo?.lat != null ? <MapLink address={op.address} geo={op.geo} /> : null} />
          {isJob && event.jobInfo && <Row icon={Info} label="Job information" value={<span className="whitespace-pre-wrap">{event.jobInfo}</span>} />}
          <Row icon={Info} label={t("cal.access", "Access")} value={op.accessInfo} />
          <Row icon={Car} label={t("cal.parking", "Parking")} value={op.parkingInfo} />
          <Row icon={DoorOpen} label={t("cal.entrance", "Entrance")} value={op.entranceInfo} />
          <Row icon={Navigation} label={t("cal.what3words", "what3words")} value={op.what3words} />
          <Row icon={Info} label={t("cal.instructions", "Instructions")} value={op.instructions} />
          <Row icon={ListChecks} label={t("cal.checklist", "Checklist template")} value={op.checklistTemplateId} />
          {op.geo?.lat != null && (
            <Row icon={MapPin} label="GPS" value={`${op.geo.lat}, ${op.geo.lng}${op.geofenceRadius ? ` · ${op.geofenceRadius}m` : ""}`} />
          )}
          {!isJob && <Row icon={Info} label={t("sched.time", "Hours")} value={`${op.openingTime || ""}–${op.closingTime || ""}`} />}
        </div>

        {(isJob || isGroup) && event.workers?.length > 0 && (
          <div className="space-y-1.5 rounded-lg bg-slate-50 p-3" data-testid="detail-workers">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("job.workers", "Workers")}</p>
            <div className="flex flex-wrap gap-1.5">
              {event.workers.map((w, i) => (
                <Badge key={w.id} variant="outline" className="border-slate-300 text-slate-700">
                  {w.name}{isJob && i === 0 ? " ★" : ""}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <ShiftClockIn event={event} />

        {editable ? (
          <div className="space-y-3">
            <div>
              <Label>{t("cal.reassign", "Reassign worker")}</Label>
              {isJob ? (
                <>
                  <WorkerMultiSelect workers={workers} value={form.workerIds} onChange={(v) => setForm({ ...form, workerIds: v })} testid="detail-workers-multi" />
                  <p className="text-[11px] text-slate-400 mt-1">{t("job.multiHint", "Assign one or more workers. Each records their own time.")}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Label className="text-xs">{t("stf.required", "Required workers")}</Label>
                    <Input type="number" min="1" data-testid="detail-required-workers" value={form.requiredWorkerCount}
                      onChange={(e) => setForm({ ...form, requiredWorkerCount: Math.max(1, Number(e.target.value) || 1) })} className="w-20 h-8" />
                    <StaffingBadge assigned={form.workerIds.length} required={form.requiredWorkerCount} testid="detail-staffing" />
                  </div>
                </>
              ) : isGroup ? (
                <>
                  <WorkerMultiSelect workers={workers.filter((w) => w.operationalAccess !== "FIELD_ONLY")} value={form.workerIds} onChange={(v) => setForm({ ...form, workerIds: v })} testid="detail-shift-workers-multi" />
                  <p className="text-[11px] text-slate-400 mt-1">One shift, several cleaners — each still clocks in and records their own time.</p>
                </>
              ) : (
                <Select value={form.workerId} onValueChange={(v) => setForm({ ...form, workerId: v })}>
                  <SelectTrigger data-testid="detail-worker-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>{t("job.unassigned", "Unassigned")}</SelectItem>
                    {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.displayName}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>{t("cal.date", "Date")}</Label><Input type="date" data-testid="detail-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t("cal.start", "Start")}</Label><Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t("cal.end", "End")}</Label><Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="mt-1.5" /></div>
            </div>

            {event.seriesId && (
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={applySeries} onCheckedChange={setApplySeries} data-testid="apply-series-checkbox" />
                {t("cal.applySeries", "Apply to this and future occurrences")}
              </label>
            )}

            <div className="border-t border-slate-100 pt-3">
              <button type="button" data-testid="toggle-recur-btn" onClick={() => setShowRecur((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-brand hover:underline">
                <Repeat className="h-4 w-4" />{event.seriesId ? t("cal.changeRepeat", "Change repeat from here") : t("cal.makeRepeat", "Make this repeat")}
              </button>
              {showRecur && (
                <div className="space-y-3 rounded-lg bg-slate-50 p-3 mt-2" data-testid="detail-recurrence-panel">
                  <p className="text-[11px] text-slate-500">{t("cal.repeatFromHereHint", "Applies from this date forward only — earlier occurrences are unchanged.")}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("cal.freq", "Frequency")}</Label>
                      <Select value={rec.freq} onValueChange={(v) => setRec({ ...rec, freq: v })}>
                        <SelectTrigger data-testid="detail-recur-freq" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">{t("cal.daily", "Daily")}</SelectItem>
                          <SelectItem value="weekly">{t("cal.weekly", "Weekly")}</SelectItem>
                          <SelectItem value="monthly">{t("cal.monthly", "Monthly")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t("cal.interval", "Every")}</Label><Input type="number" min="1" data-testid="detail-recur-interval" value={rec.interval} onChange={(e) => setRec({ ...rec, interval: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  {rec.freq === "weekly" && (
                    <div>
                      <Label>{t("cal.weekdays", "On days")}</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {WEEKDAYS.map((d) => (
                          <button key={d} type="button" data-testid={`detail-recur-wd-${d}`} onClick={() => toggleWd(d)}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${rec.byWeekday.includes(d) ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                            {t(`wd.${d}`, ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d])}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <NeverEnds rec={rec} setRec={setRec} testid="detail-recur-forever" />
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("cal.endsAfter", "Ends after")}</Label><Input type="number" min="1" data-testid="detail-recur-count" value={rec.count} disabled={!!rec.until || rec.forever} onChange={(e) => setRec({ ...rec, count: e.target.value })} className="mt-1.5" placeholder={t("cal.occurrences", "occurrences")} /></div>
                    <div><Label>{t("cal.endsOn", "Ends on")}</Label><Input type="date" data-testid="detail-recur-until" value={rec.until} disabled={rec.forever} onChange={(e) => setRec({ ...rec, until: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <Button size="sm" data-testid="detail-recur-apply" onClick={rescheduleSeries} disabled={busy} className="bg-brand hover:bg-brand-dark">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cal.applyRepeat", "Apply repeat from here")}
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">{t("cal.changeStatus", "Change status")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button size="sm" variant="outline" data-testid="status-active-btn" onClick={() => patch({ status: "active" })} disabled={busy || event.status === "active"}>{t("cal.markActive", "Mark active")}</Button>
                <Button size="sm" variant="outline" data-testid="status-completed-btn" onClick={() => patch({ status: "completed" })} disabled={busy || event.status === "completed"}>{t("cal.markCompleted", "Mark completed")}</Button>
              </div>
              {event.status !== "cancelled" && (
                <div className="mt-2 flex gap-2 items-end">
                  <div className="flex-1"><Label className="text-xs">{t("cal.cancelReason", "Cancellation reason")}</Label>
                    <Input data-testid="cancel-reason-input" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="mt-1.5" /></div>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="status-cancel-btn"
                    onClick={() => patch({ status: "cancelled" })} disabled={busy || !cancelReason}>{t("cal.markCancelled", "Cancel")}</Button>
                </div>
              )}
              {event.cancellation?.reason && <p className="text-xs text-red-600 mt-2">{event.cancellation.reason}</p>}
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-600">
            <p><Badge variant="outline">{t(`st.${event.status}`, event.status)}</Badge></p>
            <p className="mt-2">{event.date} · {event.startTime}–{event.endTime}</p>
            {event.workerName && <p className="mt-1">{t("cal.worker", "Worker")}: {event.workerName}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setEditMode(false)} data-testid="detail-back-summary-btn">{t("common.back", "Back")}</Button>
          {isJob && can("job.manage") && onDuplicate && (
            <Button variant="outline" data-testid="detail-duplicate-btn" onClick={() => onDuplicate(event)} disabled={busy} className="text-brand border-brand/30 hover:bg-brand/5">
              <Repeat className="h-4 w-4 mr-1.5" />{t("cal.duplicate", "Duplicate")}
            </Button>
          )}
          {isJob && onQueue && !event.seriesId && event.status === "scheduled" && (
            <Button variant="outline" data-testid="detail-queue-btn" onClick={() => onQueue(event)} disabled={busy} className="text-violet-700 border-violet-300 hover:bg-violet-50">
              Move to queue
            </Button>
          )}
          {editable && (
            <Button variant="outline" data-testid="detail-delete-btn" onClick={remove} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="h-4 w-4 mr-1.5" />{applySeries && event.seriesId ? t("cal.deleteSeries", "Delete series") : t("common.delete", "Delete")}
            </Button>
          )}
          {editable && (
            <Button data-testid="detail-save-btn" onClick={() => patch()} disabled={busy} className="bg-brand hover:bg-brand-dark">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cal.save", "Save changes")}
            </Button>
          )}
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
    {isJob && (
      <Dialog open={showJob} onOpenChange={setShowJob}>
        <DialogContent className="max-w-5xl w-[96vw] max-h-[92vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)] p-4 sm:p-6" data-testid="job-details-window">
          <DialogHeader className="sr-only"><DialogTitle>Job details</DialogTitle><DialogDescription>Visits, hours, notes and photos</DialogDescription></DialogHeader>
          {showJob && <JobProfile jobId={event.id} embedded />}
        </DialogContent>
      </Dialog>
    )}
    </>
  );
}
